import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import DeliveryTracker from '../components/DeliveryTracker';
import { motion } from 'framer-motion';

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const api = useApi();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        setError('Error retrieving document.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, api]);

  if (loading) return (
    <div className="min-h-screen bg-deepDark flex flex-col items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mb-4"></div>
      <p className="text-textMuted text-xs tracking-widest uppercase">Retrieving...</p>
    </div>
  );
  if (error || !order) return <div className="text-center py-20 text-white min-h-screen bg-deepDark pt-32">{error || 'Document not found'}</div>;

  const getStatusStyle = (status) => {
    const s = status.toLowerCase();
    if (['delivered'].includes(s)) return 'text-gold border-gold';
    if (['cancelled'].includes(s)) return 'text-red-500 border-red-500';
    return 'text-white border-white/30';
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="min-h-screen bg-deepDark py-16 px-4">
      <div className="max-w-4xl mx-auto">
        
        <div className="mb-8">
          <Link to="/profile/orders" className="text-textMuted hover:text-gold text-[10px] tracking-widest uppercase transition-colors">
            ← Back to Archive
          </Link>
        </div>

        <div className="mb-12 border-b border-white/10 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-serif text-white tracking-wide mb-2">Acquisition Details</h1>
            <p className="text-textMuted font-serif italic">No. {order.orderNumber}</p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-[10px] tracking-widest uppercase text-textMuted mb-2">{new Date(order.createdAt).toLocaleDateString()}</p>
            <span className={`inline-block px-4 py-1.5 border text-xs tracking-widest uppercase font-bold ${getStatusStyle(order.status)}`}>
              {order.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Items Col */}
          <div className="lg:col-span-2 space-y-12">
            
            <section>
              <h2 className="text-xs text-textMuted tracking-widest uppercase mb-6 flex items-center gap-4">
                Manifest <span className="flex-1 h-[1px] bg-white/10"></span>
              </h2>
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex gap-6 p-6 bg-surfaceDark border border-white/5 items-center">
                    <div className="w-20 h-20 bg-[#0a0a0a] flex-shrink-0 border border-white/5">
                      {item.image && <img src={item.image} alt={item.productName} className="w-full h-full object-cover opacity-90" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-white tracking-wide text-lg mb-1">{item.productName}</h3>
                      <p className="text-[10px] tracking-widest uppercase text-textMuted">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-gold text-lg">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {order.deliveryId && (
              <section>
                <h2 className="text-xs text-textMuted tracking-widest uppercase mb-6 flex items-center gap-4">
                  Logistics Tracking <span className="flex-1 h-[1px] bg-white/10"></span>
                </h2>
                <div className="bg-surfaceDark border border-white/5 p-6">
                  <DeliveryTracker orderId={orderId} />
                </div>
              </section>
            )}

          </div>

          {/* Sidebar */}
          <div className="space-y-12">
            
            <section>
              <h2 className="text-xs text-textMuted tracking-widest uppercase mb-6 flex items-center gap-4">
                Financials <span className="flex-1 h-[1px] bg-white/10"></span>
              </h2>
              <div className="bg-surfaceDark border border-white/5 p-8">
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[10px] tracking-widest uppercase text-textMuted">
                    <span>Subtotal</span>
                    <span className="text-white">₹{(order.totalAmount - order.tax - order.deliveryCharge).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[10px] tracking-widest uppercase text-textMuted">
                    <span>Duties (10%)</span>
                    <span className="text-white">₹{order.tax?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between text-[10px] tracking-widest uppercase text-textMuted">
                    <span>Insured Delivery</span>
                    <span className="text-white">₹{order.deliveryCharge}</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-6 flex justify-between items-baseline">
                  <span className="text-xs tracking-widest uppercase font-bold text-white">Total</span>
                  <span className="text-2xl font-serif text-gold">₹{order.totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xs text-textMuted tracking-widest uppercase mb-6 flex items-center gap-4">
                Destination <span className="flex-1 h-[1px] bg-white/10"></span>
              </h2>
              <div className="bg-surfaceDark border border-white/5 p-8">
                <div className="space-y-1 text-sm font-light leading-relaxed text-white">
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
                  <p className="text-textMuted">{order.shippingAddress.country}</p>
                  <p className="mt-4 text-[10px] tracking-widest uppercase text-textMuted">Contact: <span className="text-white ml-2">{order.shippingAddress.phone}</span></p>
                </div>
              </div>
            </section>

          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default OrderDetailsPage;
